/**
 * Şans Çarkı sayfasının özet hesapları ve dilim sıralaması.
 *
 * Sayfadan ayrı durur çünkü ikisi de yöneticinin kararını doğrudan etkiler:
 * "bir çevirme bize ortalama kaça mal oluyor" ile "dilimler çarkta hangi
 * sırada" soruları ekranda görünmüyordu ve JSX içinde hesaplanan bir rakam
 * doğrudan sınanamaz.
 */

/** Bir dilimin nakit karşılığı.
 *
 * Freespin **sayılmaz**: onun bedeli sağlayıcıdaki bahis hacmidir, kasadan
 * çıkan para değil. Ortalamaya karıştırılsaydı "çevirme başına maliyet"
 * rakamı freespin dilimi eklendikçe olduğundan büyük görünürdü.
 */
const cashValue = (segment) => (
  segment?.reward_type === 'cash' || segment?.reward_type === 'bonus'
    ? Number(segment.amount || 0)
    : 0
);

/**
 * Çarkın dağılım özeti.
 *
 * Olasılık payandası `wheel_segments.weight`tir ve yalnızca **aktif**
 * dilimler yarışır (services/wheel.py `pick_segment`); pasif dilim çarkta
 * hiç çizilmez. Ağırlığı sıfır olan aktif dilim çarkta görünür ama asla
 * gelmez — sayfanın onu ayrıca sayması bu yüzdendir.
 */
export const segmentStats = (segments = []) => {
  const active = segments.filter((segment) => segment.active);
  const totalWeight = active.reduce(
    (sum, segment) => sum + Number(segment.weight || 0), 0);
  const deadWeight = active.filter(
    (segment) => Number(segment.weight || 0) <= 0).length;

  const share = (segment) => (
    totalWeight > 0 ? Number(segment.weight || 0) / totalWeight : 0);

  const chanceOf = (types) => active
    .filter((segment) => types.includes(segment.reward_type))
    .reduce((sum, segment) => sum + share(segment), 0);

  return {
    total: segments.length,
    activeCount: active.length,
    totalWeight,
    deadWeight,
    // Çevirme başına beklenen nakit/bonus ödülü.
    expectedCost: active.reduce(
      (sum, segment) => sum + share(segment) * cashValue(segment), 0),
    blankChance: chanceOf(['none']),
    freespinChance: chanceOf(['freespin']),
    spinnable: totalWeight > 0,
  };
};

/** Tek bir dilimin kazanma olasılığı (0–1). */
export const segmentChance = (segment, totalWeight) => (
  totalWeight > 0 && segment?.active
    ? Number(segment.weight || 0) / totalWeight
    : 0
);

/**
 * Dilimi listede bir sıra yukarı/aşağı taşır.
 *
 * Sıra alanı (`order`) canlı kurulumlarda çoğunlukla eşittir (hepsi 0) —
 * yalnızca iki dilimin `order` değerini takas etmek orada hiçbir şeyi
 * değiştirmezdi. Bu yüzden liste baştan numaralandırılır ve **yalnızca
 * değeri gerçekten değişen** dilimler kaydedilir: her taşımada bütün çarkı
 * sunucuya yazmak gereksiz bir yük olurdu.
 */
export const reorderSegments = (segments = [], index, direction) => {
  const target = index + direction;
  if (index < 0 || index >= segments.length
      || target < 0 || target >= segments.length) {
    return { items: segments, changed: [] };
  }
  const items = [...segments];
  [items[index], items[target]] = [items[target], items[index]];

  const changed = [];
  const renumbered = items.map((segment, position) => {
    if (Number(segment.order || 0) !== position) {
      changed.push({ id: segment.id, order: position });
    }
    return { ...segment, order: position };
  });
  return { items: renumbered, changed };
};
